/**
 * Constants for the "upgrade to Bookings/Commerce" upsell shown to WSB
 * merchants who sit on a capped monthly appointment allowance.
 *
 * Background: `onecom_wsb` merchants get a monthly appointment-creation cap
 * resolved from One.com's Access Manager. Core enforces it on every create path
 * and answers `422 MONTHLY_APPOINTMENT_LIMIT_REACHED` once the cap is hit — see
 * `core/app/models/monthly_appointment_limit.rb`. The allowance and the spend
 * against it reach app-shell from the public API; see ./usage.ts.
 */

/**
 * postMessage `type` carrying upgrade pricing from the One.com parent window
 * into the app-shell iframe, in reply to
 * {@link PRICING_REQUEST_MESSAGE_TYPE}.
 *
 * The side-menu card stays hidden until this arrives, because that copy quotes a
 * price and we will not render a card with a blank amount. The dialog and the
 * appointment banners quote no price and render regardless.
 *
 * Expected payload (see `PricingMessageData` in ./pricing.ts):
 *
 *     {
 *       type: 'WSB_BOOKINGS_UPGRADE_PRICING',
 *       data: {
 *         introductoryPrice: '$ 4.99 /mo.',
 *         renewalPrice: '$ 9.99 /mo.'
 *       }
 *     }
 *
 * Money only, no plan; see ./pricing.ts.
 */
export const PRICING_MESSAGE_TYPE = 'WSB_BOOKINGS_UPGRADE_PRICING';

/**
 * postMessage `type` app-shell sends *up* to the One.com parent window to ask
 * for {@link PRICING_MESSAGE_TYPE}.
 *
 * Makes the shell's readiness the trigger. Our listener attaches when React
 * mounts, long after the iframe fires `load`, so a price posted before that is
 * dropped with nothing to prompt a retry.
 *
 * No payload, sent once. The parent may still post unprompted — the listener
 * accepts a price whether or not it was asked for.
 */
export const PRICING_REQUEST_MESSAGE_TYPE =
  'WSB_BOOKINGS_UPGRADE_PRICING_REQUEST';

/**
 * Core's calendar realtime channel, without the `=<account_id>` suffix the
 * subscriber appends.
 *
 * Core fans appointment events out on three channel shapes
 * (`core/lib/realtime/calendar/channels_builder.rb`); this is the only one with
 * no date in it, so a session binds it once instead of tracking which days it
 * cares about. Account-scoped because `merchant_account_channel_auth` requires
 * the channel id to equal the token's subject.
 */
export const CALENDAR_CHANNEL = 'private-channel.calendar.merchant_accounts';

/**
 * Core's appointment-created event on {@link CALENDAR_CHANNEL}. Carries an
 * appointment id and its merchant, no counts — see
 * `subscriptions/appointment-created-push.ts` for what the shell does with it
 * and what Core leaves out of it.
 */
export const APPOINTMENT_CREATED_EVENT = 'appointments.v2.created';

/**
 * sessionStorage key marking the dialog as already shown this session. The
 * design asks for "once per session" for both the approaching and the reached
 * state, tracked separately so a merchant who crosses both thresholds in one
 * session still sees the more urgent one.
 */
export const DIALOG_SEEN_STORAGE_KEY = 'bookingsUpgradeDialogSeen';
